import React, { useState, useEffect } from "react";

// material ui components
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import IOSSwitch from "../../../../components/shared/ios-switch";
import Button from "@material-ui/core/Button";
import { CloseRounded } from "@material-ui/icons";
import CheckRounded from "@material-ui/icons/CheckRounded";
import Skeleton from "@material-ui/lab/Skeleton";
import swal from "sweetalert";
import { GENERAL_URL_API } from "../../../../shared/urls";
import { paisesAutocomplete } from "../../../../shared/data/paises";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { IconButton, InputAdornment, Tooltip } from "@material-ui/core";
import { generatePasswords } from "../../../../shared/utils/generatePassword";
import LockRounded from "@material-ui/icons/LockRounded";

const fData = (selectedOption) => ({
  nombreCompleto: {
    value: selectedOption?.nombreCliente || "",
    error: false,
    required: true,
  },
  correo: {
    value: selectedOption?.emailIndividual || "",
    error: false,
    required: true,
  },
  contrasena: {
    value: selectedOption?.passwd || "",
    error: false,
    required: false,
    show: false,
  },
  pais: {
    value: selectedOption?.pais || "",
    error: false,
    required: true,
  },
  suscripcionBoletin: {
    value:
      selectedOption?.boletin === undefined
        ? false
        : Boolean(Number(selectedOption?.boletin)),
    error: false,
    required: false,
  },
});

const EditarPerfilIndividualForm = ({ selectedOption, clearSelection }) => {
  const [loading, setLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [formData, setFormData] = useState(fData(selectedOption));
  const [institutions, setInstitutions] = useState([]);
  const [institutionsLoaded, setInstitutionsLoaded] = useState(false);
  const [institutionAssignments, setInstitutionAssignments] = useState(() => [
    createEmptyAssignment(),
  ]);

  useEffect(() => {
    fetchInstitutions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setFormData(fData(selectedOption));
  }, [selectedOption]);

  useEffect(() => {
    if (!institutionsLoaded) return;

    if (!selectedOption) {
      setInstitutionAssignments([createEmptyAssignment()]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const seeds = parseSelectedOptionInstitutions(selectedOption);

    if (!seeds.length) {
      setInstitutionAssignments([createEmptyAssignment()]);
      setLoading(false);
      return;
    }

    const assignmentsWithSeed = seeds.map((seed, index) => {
      const matchedInstitution =
        institutions.find((institution) => institution.nombre === seed.instituteName) || null;
      const assignmentId = `${seed.instituteName || "institucion"}-${index}-${Date.now()}-${Math.random()}`;

      return {
        assignment: {
          ...createEmptyAssignment(),
          id: assignmentId,
          institucion: {
            value: matchedInstitution,
            error: !matchedInstitution,
          },
          usuario: {
            value: null,
            error: false,
          },
          users: [],
          loadingUsers: Boolean(matchedInstitution),
        },
        seedUser: seed.userName || null,
      };
    });

    setInstitutionAssignments(assignmentsWithSeed.map(({ assignment }) => assignment));

    assignmentsWithSeed.forEach(({ assignment, seedUser }) => {
      if (assignment.institucion.value) {
        fetchInstitutionUsers(
          assignment.id,
          assignment.institucion.value.nombre,
          seedUser
        );
      }
    });

    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionsLoaded, institutions, selectedOption]);

  const handleChange = (e, inName, inValue) => {
    const name = inName || e.target.name;
    const value = inValue === undefined ? e.target.value : inValue;

    setFormData({
      ...formData,
      [name]: {
        ...formData[name],
        value,
      },
    });
  };

  const changeSwitch = (e) => {
    const name = e.target.name;
    setFormData({
      ...formData,
      [name]: {
        ...formData[name],
        value: !formData[name].value,
      },
    });
  };

  const addAssignmentRow = () => {
    setInstitutionAssignments((prev) => [...prev, createEmptyAssignment()]);
  };

  const removeAssignmentRow = (assignmentId) => {
    setInstitutionAssignments((prev) => {
      if (prev.length === 1) {
        return prev;
      }
      return prev.filter((assignment) => assignment.id !== assignmentId);
    });
  };

  const handleAssignmentInstitutionChange = (assignmentId, institution) => {
    setInstitutionAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentId
          ? {
              ...assignment,
              institucion: {
                ...assignment.institucion,
                value: institution,
                error: false,
              },
              usuario: {
                ...assignment.usuario,
                value: null,
                error: false,
              },
              users: [],
            }
          : assignment
      )
    );

    if (institution?.nombre) {
      fetchInstitutionUsers(assignmentId, institution.nombre);
    }
  };

  const handleAssignmentUserChange = (assignmentId, user) => {
    setInstitutionAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentId
          ? {
              ...assignment,
              usuario: {
                ...assignment.usuario,
                value: user,
                error: false,
              },
            }
          : assignment
      )
    );
  };

  const fetchInstitutions = async () => {
    setLoading(true);
    fetch(`${GENERAL_URL_API}/verDatos/verInstitutosPerfilesIndividuales.php`, {
      method: "POST",
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Error fetching institutions");
        const data = await res.json();
        setInstitutions(data.data || []);
      })
      .catch(() => {
        swal({
          title: "Error",
          text: "¡Hubo un problema obteniendo los institutos!",
          icon: "error",
        });
      })
      .finally(() => {
        setInstitutionsLoaded(true);
      });
  };

  const fetchInstitutionUsers = (assignmentId, instituteName, userToSelect) => {
    if (!instituteName) return;

    setInstitutionAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentId
          ? { ...assignment, loadingUsers: true, users: [] }
          : assignment
      )
    );

    const fd = new FormData();
    fd.append("nombreInstituto", instituteName);

    fetch(`${GENERAL_URL_API}/verDatos/verUsuariosInstitutosPerfilesIndividuales.php`, {
      method: "POST",
      credentials: "include",
      body: fd,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Error fetching users");
        const data = await res.json();
        setInstitutionAssignments((prev) =>
          prev.map((assignment) => {
            if (assignment.id !== assignmentId) {
              return assignment;
            }
            const usersList = data.data || [];
            const autoSelectedUser = userToSelect
              ? usersList.find((user) => user.username === userToSelect) || null
              : assignment.usuario.value;
            return {
              ...assignment,
              users: usersList,
              loadingUsers: false,
              usuario: {
                ...assignment.usuario,
                value: autoSelectedUser,
                error: autoSelectedUser ? false : assignment.usuario.error,
              },
            };
          })
        );
      })
      .catch(() => {
        swal({
          title: "Error",
          text: "¡Hubo un problema obteniendo los usuarios del instituto!",
          icon: "error",
        });
        setInstitutionAssignments((prev) =>
          prev.map((assignment) =>
            assignment.id === assignmentId
              ? { ...assignment, loadingUsers: false }
              : assignment
          )
        );
      });
  };

  function validateEmail(email) {
    if (!email) return false;
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoadingButton(true);
    let isValid = true;
    const data = { ...formData };

    Object.keys(data).forEach((key) => {
        data[key].error = false;
    });

    if (!validateEmail(data.correo.value)) {
      data.correo.error = true;
      isValid = false;
    }
    
    for (const formElement in data) {
      if (data[formElement].required && !data[formElement].value) {
        data[formElement].error = true;
        isValid = false;
      }
    }

    setFormData(data);

    let assignmentsValid = true;
    let validatedAssignments = institutionAssignments.map((assignment) => {
      const hasInstitution = !!assignment.institucion.value;
      const hasUser = !!assignment.usuario.value;

      if (!hasInstitution || !hasUser) {
        assignmentsValid = false;
      }

      return {
        ...assignment,
        institucion: {
          ...assignment.institucion,
          error: !hasInstitution,
        },
        usuario: {
          ...assignment.usuario,
          error: !hasUser,
        },
      };
    });

    const seenInstitutions = new Set();
    const duplicateAssignmentIds = new Set();

    validatedAssignments.forEach((assignment) => {
      const instituteName = assignment.institucion.value?.nombre;
      if (!instituteName) return;

      if (seenInstitutions.has(instituteName)) {
        duplicateAssignmentIds.add(assignment.id);
      } else {
        seenInstitutions.add(instituteName);
      }
    });

    if (duplicateAssignmentIds.size) {
      assignmentsValid = false;
      validatedAssignments = validatedAssignments.map((assignment) =>
        duplicateAssignmentIds.has(assignment.id)
          ? {
              ...assignment,
              institucion: {
                ...assignment.institucion,
                error: true,
              },
            }
          : assignment
      );
      swal({
        title: "Instituciones duplicadas",
        text: "Cada institución debe seleccionarse solo una vez.",
        icon: "warning",
      });
    }

    setInstitutionAssignments(validatedAssignments);

    if (!(isValid && assignmentsValid)) {
      setLoadingButton(false);
      return;
    }

    if (isValid) {
      const fd = new FormData();
      const assignmentsPayload = validatedAssignments.map((assignment) => ({
        institucion: assignment.institucion.value.nombre,
        usuarioInstituto: assignment.usuario.value.username,
      }));

      fd.append("nombreCliente", formData.nombreCompleto.value);
      fd.append("email", formData.correo.value);
      if (formData.contrasena.value) { 
          fd.append("passwordCliente", formData.contrasena.value);
      }
      fd.append("pais", formData.pais.value);
      fd.append("boletin", +formData.suscripcionBoletin.value);
      fd.append("instituciones", JSON.stringify(assignmentsPayload));

      if (assignmentsPayload[0]) {
        fd.append("instituto", assignmentsPayload[0].institucion);
        fd.append("usuarioInstituto", assignmentsPayload[0].usuarioInstituto);
      }

      fetch(`${GENERAL_URL_API}/clientes/perfiles/editarPerfilIndividual.php`, {
        method: "POST",
        credentials: "include",
        body: fd,
      })
        .then(async (res) => {
          const result = await res.json();
          if (result.status === 1) {
            await swal({
              title: "Perfil Individual Actualizado",
              text: "El perfil se actualizó satisfactoriamente.",
              icon: "success",
            });
            clearSelection();
          } else {
            await swal({
              title: "Error",
              text: result.info || "No se pudo actualizar el perfil.",
              icon: "error",
            });
          }
        })
        .catch((e) => {
          swal({
            title: "Error de Conexión",
            text: "Hubo un problema al conectar con el servidor.",
            icon: "error",
          });
          console.error("Submit error:", e);
        })
        .finally(() => {
          setLoadingButton(false);
        });
    }
  };

  return loading ? (
    <div className="mt-2">
      <Skeleton animation="wave" />
      <Skeleton animation="wave" />
      <Skeleton variant="rect" height={58} />
      <Skeleton animation="wave" />
      <Skeleton animation="wave" />
    </div>
  ) : (
    <form onSubmit={handleSubmit}>

    <label className="admin-label-text mt-3 d-block">Correo *</label>
      <TextField
        disabled={true}
        variant="outlined"
        size="small"
        fullWidth
        type="email"
        value={formData.correo.value}
        name="correo"
        placeholder="E-mail"
        onChange={handleChange}
        error={formData.correo.error}
        helperText={formData.correo.error ? "Correo no válido" : null}
      />
        
      <label className="admin-label-text mt-3 d-block">Nombre completo *</label>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        type="text"
        value={formData.nombreCompleto.value}
        name="nombreCompleto"
        placeholder="Nombre completo"
        onChange={handleChange}
        error={formData.nombreCompleto.error}
        helperText={formData.nombreCompleto.error ? "Campo no válido" : null}
      />

      <label className="admin-label-text mt-3 d-block">Contraseña</label>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        type={formData.contrasena.show ? "text" : "password"}
        value={formData.contrasena.value}
        name="contrasena"
        placeholder="Dejar en blanco para no cambiar"
        onChange={handleChange}
        error={formData.contrasena.error}
        helperText={formData.contrasena.error ? "Campo no válido" : null}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Generar contraseña">
                <IconButton
                  aria-label="generate password"
                  onClick={() => {
                    const autoPassword = generatePasswords(1)[0];
                    setFormData((prev) => ({
                      ...prev,
                      contrasena: {
                        ...prev.contrasena,
                        value: autoPassword,
                        error: false,
                      },
                    }));
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <LockRounded />
                </IconButton>
              </Tooltip>
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setFormData(prev => ({...prev, contrasena: {...prev.contrasena, show: !prev.contrasena.show}}))}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {formData.contrasena.show ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <label className="admin-label-text mt-3 d-block">País *</label>
      <Autocomplete
        options={paisesAutocomplete}
        getOptionLabel={(option) => option}
        value={formData.pais.value}
        onChange={(e, value) => handleChange(e, "pais", value)}
        noOptionsText="Sin resultados"
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Selecciona un país"
            variant="outlined"
            size="small"
            fullWidth
            error={formData.pais.error}
            helperText={formData.pais.error ? "Campo no válido" : null}
          />
        )}
      />

      <div className="d-flex align-items-center">
        <label className="admin-label-text mt-3 d-block">Suscripción al boletín</label>
        <IOSSwitch
          name="suscripcionBoletin"
          checked={formData.suscripcionBoletin.value}
          onClick={changeSwitch}
        />
      </div>

      <div className="d-flex align-items-center justify-content-between mt-3">
        <label className="admin-label-text d-block mb-0">
          Instituciones asociadas *
        </label>
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={addAssignmentRow}
        >
          Agregar institución
        </Button>
      </div>

      {institutionAssignments.map((assignment, index) => {
        const userHelperText = assignment.usuario.error
          ? "Seleccione un usuario"
          : !assignment.institucion.value
          ? "Primero seleccione una institución"
          : !assignment.loadingUsers && assignment.users.length === 0
          ? "No hay usuarios disponibles"
          : null;

        return (
          <div
            key={assignment.id}
            className="mt-3 p-3 border rounded"
            style={{ borderColor: "#e0e0e0" }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <span className="admin-label-text">
                Institución #{index + 1}
              </span>
              {institutionAssignments.length > 1 && (
                <Tooltip title="Eliminar institución">
                  <IconButton
                    size="small"
                    aria-label="remove institution"
                    onClick={() => removeAssignmentRow(assignment.id)}
                  >
                    <CloseRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>

            <Autocomplete
              className="mt-2"
              options={institutions}
              getOptionLabel={(option) => option.nombre}
              value={assignment.institucion.value}
              onChange={(e, newVal) =>
                handleAssignmentInstitutionChange(assignment.id, newVal)
              }
              noOptionsText="Sin resultados"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={assignment.institucion.error}
                  helperText={
                    assignment.institucion.error
                      ? "Seleccione una institución"
                      : null
                  }
                />
              )}
            />

            <label className="admin-label-text mt-3 d-block">
              Usuario institución *
            </label>
            <Autocomplete
              options={assignment.users}
              getOptionLabel={(option) => option.username}
              value={assignment.usuario.value}
              onChange={(e, newVal) =>
                handleAssignmentUserChange(assignment.id, newVal)
              }
              noOptionsText={
                assignment.loadingUsers ? "Cargando usuarios..." : "Sin resultados"
              }
              disabled={!assignment.institucion.value || assignment.loadingUsers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={assignment.usuario.error}
                  helperText={userHelperText}
                />
              )}
            />
          </div>
        );
      })}

      <div className="mt-3 w-100 d-flex justify-content-end">
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<CheckRounded />}
          style={{ background: "#81c868" }}
          onClick={handleSubmit}
          disabled={loadingButton}
        >
          Guardar Cambios
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="ml-2"
          startIcon={<CloseRounded style={{ fontSize: "25px" }} />}
          style={{ background: "#f05050" }}
          onClick={clearSelection}
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
};

const createEmptyAssignment = () => ({
  id: `${Date.now()}-${Math.random()}`,
  institucion: {
    value: null,
    error: false,
  },
  usuario: {
    value: null,
    error: false,
  },
  users: [],
  loadingUsers: false,
});

const parseSelectedOptionInstitutions = (option) => {
  if (!option) return [];

  const candidates =
    option.institucionesAsociadas ||
    option.instituciones_asociadas ||
    option.instituciones ||
    option.institutos ||
    option.institucionesRelacionadas ||
    option.instituciones_relacionadas ||
    option.institucionesAsignadas ||
    option.instituciones_asignadas;

  if (Array.isArray(candidates) && candidates.length) {
    return candidates
      .map((item) => ({
        instituteName:
          item?.nombre ||
          item?.instituto ||
          item?.institute ||
          item?.name ||
          item?.institucion,
        userName:
          item?.usuario ||
          item?.usuarioInstituto ||
          item?.usuario_padre ||
          item?.username ||
          item?.usuarioAsociado,
      }))
      .filter((item) => !!item.instituteName);
  }

  const fallbackInstitute =
    option.instituto ||
    option.institute ||
    option.nombreInstituto ||
    option.institucion;
  const fallbackUser =
    option.usuario_padre ||
    option.usuarioInstituto ||
    option.usuario ||
    option.usuarioAsociado ||
    option.username;

  if (fallbackInstitute) {
    return [
      {
        instituteName: fallbackInstitute,
        userName: fallbackUser || null,
      },
    ];
  }

  return [];
};

export default EditarPerfilIndividualForm; 